import { useEffect, useState } from "react";
import axios from "axios";
import CustomBarChart from "../Charts/CustomBarChart";
import CommonCard from "../ui/CommonCard/CommonCard";
import classes from "./LabDashboard.module.css";
import { useAuth } from "../../context/AuthContext";
import { env } from "../../constants/env";
import { db } from "../../utils/offlineDB";

const colorPalette = ["#C607CA", "#1302FB", "#1CDF03", "#FE9900", "#E60101"];

const LabDashboard = () => {
  const { user, token } = useAuth();
  const labId = user?.labId;

  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLab = async () => {
      setLoading(true);
      try {
        // 1️⃣ Check for local lab data
        const localLab = await db.offlineLabs.get(labId);

        // Use local if newer than last synced
        if (
          localLab &&
          (!localLab.lastSynced ||
            new Date(localLab.timestamp) > new Date(localLab.lastSynced))
        ) {
          console.log("Using newer local lab data");
          setLab(localLab);
          setLoading(false);
          return;
        }

        // If offline, use local only
        if (!navigator.onLine) {
          if (localLab) setLab(localLab);
          else setError("No offline data available");
          setLoading(false);
          return;
        }

        // Online: fetch from server
        const res = await axios.get(`${env.kataApiUrl}/api/labs/${labId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const serverLab = res.data;
        setLab(serverLab);

        // Save/update IndexedDB
        await db.offlineLabs.put({
          ...serverLab,
          id: labId,
          offline: false,
          lastSynced: new Date().toISOString(),
          timestamp: Date.now(),
        });
      } catch (err) {
        console.error("Failed to fetch lab:", err);
        if (!lab) setError("Failed to fetch lab");
      } finally {
        setLoading(false);
      }
    };

    if (labId) fetchLab();
  }, [labId, token]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!lab) return <p>Lab not found</p>;

  // Bar chart data
  const staffDistribution = [
    { name: "Degree", number: lab.degreeHolders },
    { name: "Diploma", number: lab.diplomaHolders },
    { name: "Certificate", number: lab.certificateHolders },
    { name: "Clerks", number: lab.dataClerks },
    { name: "Phlebotomists", number: lab.phlebotomists },
    { name: "Cleaners", number: lab.cleaners },
    { name: "Drivers", number: lab.drivers },
    { name: "Others", number: lab.others },
  ];

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "-";

  const today = new Date();
  const isValidDate = (dateStr) =>
    dateStr ? !isNaN(new Date(dateStr).getTime()) : false;
  const isFutureOrToday = (dateStr) =>
    isValidDate(dateStr) && new Date(dateStr) >= today;

  const itemList = [
    {
      label: "HPCZ License status",
      labelList: ["Expiry date", "Validity period", "License number"],
      status: isFutureOrToday(lab.hpcz?.expiryDate) ? "Active" : "Inactive",
      statusColor: isFutureOrToday(lab.hpcz?.expiryDate)
        ? "#1CDF03"
        : "#BA3737",
      statusBackground: isFutureOrToday(lab.hpcz?.expiryDate)
        ? "rgba(28, 223, 3, 0.2)"
        : "rgba(186, 55, 55, 0.2)",
      valueList: [
        formatDate(lab.hpcz?.expiryDate) || "N/A",
        lab.hpcz?.validityPeriod || "N/A",
        lab.hpcz?.licenseNumber || "N/A",
      ],
    },
    {
      label: "Certification (SLIPTA) status",
      labelList: ["Last certified", "Next due"],
      status: isFutureOrToday(lab.slipta?.nextDue) ? "Certified" : "Expired",
      statusColor: isFutureOrToday(lab.slipta?.nextDue)
        ? "#1CDF03"
        : "#BA3737",
      statusBackground: isFutureOrToday(lab.slipta?.nextDue)
        ? "rgba(28, 223, 3, 0.2)"
        : "rgba(186, 55, 55, 0.2)",
      valueList: [
        formatDate(lab.slipta?.lastCertified),
        formatDate(lab.slipta?.nextDue),
      ],
    },
    {
      label: "Accreditation (SADCAS) status",
      labelList: ["Last assessment", "Next due"],
      status: isFutureOrToday(lab.sadcas?.nextDue) ? "Accredited" : "Expired",
      statusColor: isFutureOrToday(lab.sadcas?.nextDue)
        ? "#1CDF03"
        : "#BA3737",
      statusBackground: isFutureOrToday(lab.sadcas?.nextDue)
        ? "rgba(28, 223, 3, 0.2)"
        : "rgba(186, 55, 55, 0.2)",
      valueList: [
        formatDate(lab.sadcas?.lastAssessment),
        formatDate(lab.sadcas?.nextDue),
      ],
    },
  ];

  return (
    <div className={classes.wrapper}>
      <h2>Dashboard</h2>

      {/* Bar Chart */}
      <CommonCard>
        <div className={classes.cardHeader}>
          <h5>Laboratory staff distribution</h5>
        </div>
        <CustomBarChart data={staffDistribution} colors={colorPalette} />
      </CommonCard>

      {/* Info Cards */}
      <div className="row mt-3">
        {itemList.map((item, i) => (
          <div className="col-4" key={i}>
            <CommonCard className={classes.certificationCard}>
              <div className="row">
                <div className={`${classes.labelsContainer} col-6`}>
                  <div
                    className={`d-flex align-items-center ${classes.certificationCardHeader} mb-3`}
                  >
                    <h5>{item.label}</h5>
                  </div>
                  {item.labelList.map((label, i) => (
                    <p className="mb-2" key={i}>
                      {label}
                    </p>
                  ))}
                </div>
                <div className={`${classes.valuesContainer} col-6 text-end`}>
                  <div className="d-flex justify-content-end">
                    <div
                      className={`d-flex align-items-center gap-2 ${classes.statusCard} mb-3`}
                      style={{ backgroundColor: item.statusBackground }}
                    >
                      <div
                        className={classes.statusDot}
                        style={{ backgroundColor: item.statusColor }}
                      ></div>
                      {item.status}
                    </div>
                  </div>
                  {item.valueList.map((value, i) => (
                    <p className="mb-2" key={i}>
                      {value}
                    </p>
                  ))}
                </div>
              </div>
              {item.downloadButton && (
                <div className={classes.downloadButton} role="button">
                  {item.downloadButton.text}
                </div>
              )}
            </CommonCard>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LabDashboard;